const fallbackPalette = {
  primary: '#006F8C',
  surface: '#FFFFFF',
  border: '#D8E1EA',
  text: '#19324D',
  muted: '#6B7C93',
  success: '#198754',
  warning: '#B7791F',
  danger: '#C2414C'
}

const paletteToken = (token, fallback) => {
  if(typeof window === 'undefined' || !window.getComputedStyle) {
    return fallback
  }

  const value = window.getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim()

  return value || fallback
}

const getPalette = () => ({
  primary: paletteToken('--app-color-primary', fallbackPalette.primary),
  surface: paletteToken('--app-color-surface', fallbackPalette.surface),
  border: paletteToken('--app-color-border', fallbackPalette.border),
  text: paletteToken('--app-color-text-secondary', fallbackPalette.text),
  muted: paletteToken('--app-color-text-muted', fallbackPalette.muted),
  success: paletteToken('--app-color-success', fallbackPalette.success),
  warning: paletteToken('--app-color-warning', fallbackPalette.warning),
  danger: paletteToken('--app-color-danger', fallbackPalette.danger)
})

const isObject = (value) => value && Object.prototype.toString.call(value) === '[object Object]'

const clone = (value) => {
  if(Array.isArray(value)) {
    return value.map(clone)
  }
  if(isObject(value)) {
    return Object.keys(value).reduce((result, key) => {
      result[key] = clone(value[key])
      return result
    }, {})
  }
  return value
}

// Combina defaults con la configuración remota sin mutar el payload del backend.
// Para los arrays de ejes se preserva cada eje enviado y solo se completan sus claves faltantes.
const mergeOptions = (defaults, options) => {
  const merged = clone(defaults)

  if(!isObject(options)) {
    return merged
  }

  Object.keys(options).forEach((key) => {
    const value = options[key]
    const defaultValue = defaults[key]

    if(isObject(value) && isObject(defaultValue)) {
      merged[key] = mergeOptions(defaultValue, value)
      return
    }

    if(Array.isArray(value) && Array.isArray(defaultValue)) {
      merged[key] = value.map((item, index) => {
        if(isObject(item) && isObject(defaultValue[index])) {
          return mergeOptions(defaultValue[index], item)
        }
        return clone(item)
      })
      return
    }

    merged[key] = clone(value)
  })

  return merged
}

const formatChartNumber = (value) => {
  const number = Number(value)
  if(!Number.isFinite(number)) {
    return value
  }

  return new Intl.NumberFormat('es-AR', {
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(number)
}

const formatTooltipNumber = (value) => {
  const number = Number(value)
  if(!Number.isFinite(number)) {
    return value
  }

  return new Intl.NumberFormat('es-AR', {
    maximumFractionDigits: 2
  }).format(number)
}

const resolveTooltipValue = (tooltipItem, chartData) => {
  const dataset = chartData.datasets[tooltipItem.datasetIndex] || {}
  const values = Array.isArray(dataset.data) ? dataset.data : []
  const rawValue = values[tooltipItem.index]

  if(isObject(rawValue)) {
    if(rawValue.y !== undefined) return rawValue.y
    if(rawValue.x !== undefined) return rawValue.x
    if(rawValue.value !== undefined) return rawValue.value
  }

  if(rawValue !== undefined) {
    return rawValue
  }

  if(tooltipItem.yLabel !== undefined) return tooltipItem.yLabel
  if(tooltipItem.xLabel !== undefined) return tooltipItem.xLabel

  return ''
}

const replaceLegacyColor = (color, palette) => {
  if(typeof color !== 'string') {
    return color
  }

  const colors = {
    blue: palette.primary,
    dodgerblue: palette.primary,
    green: palette.success,
    red: palette.danger,
    orange: palette.warning,
    yellow: palette.warning
  }

  return colors[color.trim().toLowerCase()] || color
}

export const createChartData = (data) => {
  const palette = getPalette()
  const chartData = clone(data || {})

  if(!Array.isArray(chartData.datasets)) {
    return chartData
  }

  chartData.datasets = chartData.datasets.map((dataset) => {
    const normalized = clone(dataset)

    const colorKeys = ['backgroundColor', 'borderColor', 'hoverBackgroundColor', 'hoverBorderColor']
    colorKeys.forEach((key) => {
      if(Array.isArray(normalized[key])) {
        normalized[key] = normalized[key].map((color) => replaceLegacyColor(color, palette))
      } else {
        normalized[key] = replaceLegacyColor(normalized[key], palette)
      }
    })

    return normalized
  })

  return chartData
}

export const createChartOptions = (type, options) => {
  const palette = getPalette()
  const defaults = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 280,
      easing: 'easeOutQuart'
    },
    layout: {
      padding: {
        top: 4,
        right: 4,
        bottom: 2,
        left: 2
      }
    },
    legend: {
      position: 'bottom',
      labels: {
        boxWidth: 10,
        padding: 14,
        fontColor: palette.muted,
        fontFamily: 'Inter, sans-serif',
        fontSize: 11,
        usePointStyle: true
      }
    },
    tooltips: {
      backgroundColor: palette.text,
      titleFontFamily: 'Inter, sans-serif',
      bodyFontFamily: 'Inter, sans-serif',
      titleFontSize: 12,
      bodyFontSize: 12,
      xPadding: 10,
      yPadding: 9,
      cornerRadius: 7,
      displayColors: true,
      callbacks: {
        label: (tooltipItem, chartData) => {
          const dataset = chartData.datasets[tooltipItem.datasetIndex] || {}
          const labels = Array.isArray(chartData.labels) ? chartData.labels : []
          const itemLabel = labels[tooltipItem.index]
          const label = (type === 'pie' || type === 'doughnut')
            ? itemLabel
            : (dataset.label || itemLabel)
          const value = resolveTooltipValue(tooltipItem, chartData)

          return (label ? label + ': ' : '') + formatTooltipNumber(value)
        }
      }
    }
  }

  if(type === 'bar') {
    defaults.hover = { mode: 'index', intersect: false }
    defaults.scales = {
      xAxes: [{
        gridLines: {
          display: false,
          drawBorder: false
        },
        ticks: {
          fontColor: palette.muted,
          fontFamily: 'Inter, sans-serif',
          fontSize: 11,
          padding: 8,
          autoSkip: true,
          maxTicksLimit: 8,
          maxRotation: 0,
          minRotation: 0
        }
      }],
      yAxes: [{
        gridLines: {
          color: palette.border,
          drawBorder: false,
          zeroLineColor: palette.border
        },
        ticks: {
          fontColor: palette.muted,
          fontFamily: 'Inter, sans-serif',
          fontSize: 11,
          padding: 8,
          callback: formatChartNumber
        }
      }]
    }
  }

  if(type === 'pie' || type === 'doughnut') {
    defaults.elements = {
      arc: {
        borderColor: palette.surface,
        borderWidth: 2
      }
    }
  }

  if(type === 'doughnut') {
    defaults.cutoutPercentage = 62
  }

  return mergeOptions(defaults, options || {})
}
